import type { InsuranceCheckoutData } from '@/uTypes/order/insurance'
import { providerFormat } from '@/utils/formats'
// import airbridge from 'airbridge-web-sdk-loader'
interface InitializeOptions {
  app: string
  webToken: string
  autoStartTrackingEnabled?: boolean
  utmParsing?: boolean
  utmParameterValueReplaceMap?: Record<string, Record<string, string>>
  urlQueryMapping?: Record<string, string>
  userHash?: boolean
  cookieWindow?: number
  cookieWindowInMinutes?: number
  useProtectedAttributionWindow?: boolean
  protectedAttributionWindowInMinutes?: number
  shareCookieSubdomain?: boolean
  collectMolocoCookieID?: boolean
}

interface AirbridgeEventPayload {
  action?: string
  label?: string
  value?: number
  semanticAttributes?: object
  customAttributes?: object
}

const getAccoutUserInfo = async () => {
  const { getUserAccountInfo } = useApi()
  const user: { data: any; success: boolean } = await getUserAccountInfo()

  const userInfo = {
    userId: user.data?.accountId || '-',
    userType: user.data?.accountCode || 'guest'
  }

  return userInfo
}

export const useAirbridge = () => {
  if (import.meta.server) {
    return {
      initAirbridge: () => {},
      sendEvent: () => {},
      trackHomeView: () => {},
      trackLogin: () => {},
      trackSignup: () => {},
      trackOrderInfo: (data?: any) => {},
      trackInsuranceOrderInfo: (data?: any) => {},
      trackOrderSuccess: (data: any) => {},
      trackInsuranceOrderSuccess: (data: any) => {},
      trackAirportTaxiCheckout: (data: any) => {},
      trackAirportTaxiOrderCompleted: (data: any, _completeLegs?: any) => {},
      trackAirportTaxiOrderCanceled: (data: any) => {},
      trackInsuranceOrderRefund: (data: any) => {},
      trackRefundOder: (data: any) => {},
      trackSimAction: (data: any) => {},
      trackBenefit: ({ eventId, eventName }: { eventId: string; eventName: string }) => {},
      trackCoupon: () => {},
      trackCouponReceive: ({ couponId, couponName }: { couponId: string; couponName: string }) => {}
    }
  }
  const config = useRuntimeConfig()
  const dayjs = useDayjs()

  // 에어브릿지 초기화
  const initAirbridge = () => {
    const options: InitializeOptions = {
      app: config.public.airbridgeAppName,
      webToken: config.public.airbridgeWebToken,
      utmParsing: true
    }

    airbridge.init(options)
  }

  // 에어브릿지 이벤트 전송
  const sendEvent = (category: string, data: AirbridgeEventPayload) => {
    airbridge.events.send(category, data)
  }

  /**************************************************************************************************
   * esim / usim  - 유심사 sim
   **************************************************************************************************/

  // 에어브릿지 홈뷰 트래킹
  const trackHomeView = () => {
    sendEvent('airbridge.ecommerce.home.viewed', {
      action: 'home_view'
    })
  }

  // 에어브릿지 로그인 트래킹
  const trackLogin = async (action: string) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    sendEvent('airbridge.user.signin', {
      action: action || '-',
      label: user.userId
    })
    // 추후 클리어 이벤트 붙여야 함
    airbridge.setUserID(user.userId)
  }

  // 에어브릿지 회원가입 트래킹
  const trackSignup = (action: string, id: string) => {
    sendEvent('airbridge.user.signup', {
      action: action || '-',
      label: id
    })
  }

  // 에어브릿지 주문 정보 트래킹
  const trackOrderInfo = async (data: any) => {
    const { checkoutItem, countQuery } = data
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    // 시멘틱 어트리뷰트
    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: checkoutItem?.planName || '-',
          price: checkoutItem?.price || 0,
          quantity: countQuery.count,
          brandName: checkoutItem?.optionName || '-'
        }
      ],
      period: checkoutItem?.days || '-',
      totalQuantity: countQuery.count,
      isRevenue: true,
      // totalValue: checkoutItem?.totalProductAmount + (checkoutItem?.shippingFee || 0),
      originalTotalValue: checkoutItem?.totalProductAmount + (checkoutItem?.shippingFee || 0)
    }
    // 커스텀 어트리뷰트
    const customAttributes = {
      productNetworktype: providerFormat(checkoutItem.provider) || '-',
      userId: user.userId,
      userType: user.userType
    }

    sendEvent('airbridge.initiateCheckout', {
      action: checkoutItem?.planName || '-',
      label: checkoutItem?.simType || '-',
      semanticAttributes,
      customAttributes
    })
  }

  // 에어브릿지 주문 성공 트래킹
  const trackOrderSuccess = async (data: any) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()
    const totalAmount = data?.totalProductAmount + (data?.shippingFee || 0)

    // 시멘틱 어트리뷰트
    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: data?.planName || '-',
          price: data?.price || 0,
          quantity: data.productCount || 1,
          brandName: data?.optionName || '-'
        }
      ],
      period: data?.days || '-',
      transactionID: data?.orderId || '-',
      transactionType: data?.payMethod || '-',
      totalQuantity: data.productCount || 1,
      inAppPurchased: data.inAppPurchased || false,
      isRevenue: true,
      totalValue: data.amount || 0,
      originalTotalValue: totalAmount || 0
    }

    // 커스텀 어트리뷰트
    const customAttributes = {
      userId: user.userId,
      userType: user.userType,
      productNetworktype: providerFormat(data.provider) || '-',
      couponDiscount: data?.coupon?.discountAmount || 0,
      couponName: data?.coupon?.selectedCoupon?.filter((i: any) => i.couponName) || [],
      pointDiscount:
        Number(data?.totalProductAmount + (data?.shippingFee || 0) || 0) -
        (Number(data?.coupon?.discountAmount || 0) + Number(data?.amount || 0)),
      actualPrice: data?.amount || 0
    }

    sendEvent('airbridge.ecommerce.order.completed', {
      action: data?.planName || '-',
      label: data?.simType || '-',
      semanticAttributes,
      customAttributes,
      value: data?.amount || 0
    })
  }

  // 에어브릿지 환불 트래킹
  const trackRefundOder = async (data: any) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    // 시멘틱 어트리뷰트
    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: data?.orders?.[0].planName || '-',
          price: data?.paymentSummary?.totalProductAmount / data.orders.length || 0,
          quantity: data.orders.length || 1,
          brandName: data?.orders?.[0].orderItems?.[0].optionName || '-'
        }
      ],
      period: data?.orders?.[0]?.orderItems?.[0]?.optionName?.split('/')?.[0] || '-',
      transactionID: (data?.orderGroup ? data?.orderGroup.orderGroupId : data?.orderId) || '-',
      transactionType: data?.paymentSummary?.payments?.[0]?.pgType || '-',
      totalQuantity: data.orders.length || 1,
      inAppPurchased: data.platformType || false,
      isRevenue: true,
      totalValue: data?.paymentSummary?.paidAmount || 0,
      originalTotalValue: data?.paymentSummary?.totalAmount || 0
    }

    // 커스텀 어트리뷰트
    const customAttributes = {
      userId: user.userId,
      userType: user.userType,
      productNetworktype: data.orders?.[0].orderType === 'ODT001' ? 'eSIM' : 'USIM', // 백엔드측에서 추가 필요
      couponDiscount: data?.paymentSummary?.couponAmount || 0,
      couponName: data?.paymentSummary?.coupons?.filter((i: any) => i.couponName) || [], // 백엔드 측에서 데이터 추가 필요 - 요청 완료(이걸 프론트에서 들고 있을 필요가..?)
      pointDiscount: data?.paymentSummary?.pointAmount || 0,
      actualPrice: data?.paymentSummary?.paidAmount || 0
    }

    sendEvent('airbridge.ecommerce.order.canceled', {
      action: data?.orders?.[0].planName || '-',
      label: data.orders?.[0].orderType === 'ODT001' ? 'eSIM' : 'USIM', // 백엔드 측에서 추가 필요
      semanticAttributes,
      customAttributes,
      value: -data?.paymentSummary?.paidAmount || 0
    })
  }

  // esim 발급 트래킹
  const trackSimAction = async (data: any) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    sendEvent('contents_action', {
      action: data || '-',
      customAttributes: user
    })
  }

  // benefit 추적 추가
  const trackBenefit = async ({ eventId, eventName }: { eventId: string; eventName: string }) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    sendEvent('benefit_view', {
      action: eventId || '-',
      label: eventName || '-',
      customAttributes: user
    })
  }

  // benefit 추적 추가
  const trackCoupon = async () => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    sendEvent('coupon_view', {
      customAttributes: user
    })
  }

  // coupon 발급 추적 추가
  const trackCouponReceive = async ({
    couponId,
    couponName
  }: {
    couponId: string
    couponName: string
  }) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    sendEvent('coupon_receive', {
      action: couponId || '-',
      label: couponName || '-',
      customAttributes: user
    })
  }

  /**************************************************************************************************
   * insurance - 여행자 보험
   **************************************************************************************************/
  const trackInsuranceOrderInfo = async (data: InsuranceCheckoutData) => {
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    const netPremium = Number(data.premium || 0)

    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: '해외여행보험',
          price: netPremium
        }
      ]
    }

    const start = data.insuranceStartDateTime ? dayjs(data.insuranceStartDateTime) : null
    const end = data.insuranceEndDateTime ? dayjs(data.insuranceEndDateTime) : null

    const customAttributes = {
      userId: user.userId,
      userType: user.userType,
      page_step: 'traveler_insurance_6.orderinfo',

      insurance_members: data.insuredCount || 0,

      insurance_startdate: start?.format('YYYY.MM.DD'),
      insurance_starttime: start?.format('HH:mm'),

      insurance_enddate: end?.format('YYYY.MM.DD'),
      insurance_endtime: end?.format('HH:mm'),

      insurance_nation: data.destinationCountryName,
      insurance_type: data.planName
    }

    sendEvent('airbridge.initiateCheckout', {
      action: '해외여행보험_결제하기',
      label: data.planName || '-',
      semanticAttributes,
      customAttributes,
      value: netPremium
    })
  }

  const trackInsuranceOrderSuccess = async (data: any) => {
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: '해외여행보험',
          price: data.premium || 0
        }
      ],
      transactionID: data?.orderGroupId || '-',
      transactionType: data?.payMethod || '-'
    }

    const insuranceStartDateTimeSplit = data.insuranceStartDateTime?.split('T')
    const insuranceEndDateTimeSplit = data.insuranceEndDateTime?.split('T')

    const customAttributes = {
      userId: user.userId,
      userType: user.userType,
      page_step: 'traveler_insurance_6.orderinfo',

      insurance_members: data.insured?.length || 0,

      insurance_startdate: insuranceStartDateTimeSplit?.[0],
      insurance_starttime: insuranceStartDateTimeSplit?.[1],

      insurance_enddate: insuranceEndDateTimeSplit?.[0],
      insurance_endtime: insuranceEndDateTimeSplit?.[1],

      insurance_nation: data.destinationCountryName,
      insurance_type: data.planName
    }

    sendEvent('airbridge.ecommerce.order.completed', {
      action: '해외여행보험_결제완료',
      label: data.planName || '-',
      semanticAttributes,
      customAttributes,
      value: data.premium || 0
    })
  }

  const trackInsuranceOrderRefund = async (data: any) => {
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    const semanticAttributes = {
      currency: 'KRW',
      products: [
        {
          productID: '해외여행보험',
          price: data.premium || 0
        }
      ],
      transactionID: data?.orderGroupId || '-',
      transactionType: data?.payMethod || '-'
    }

    const insuranceStartDateTimeSplit = data.insuranceStartDateTime?.split('T')
    const insuranceEndDateTimeSplit = data.insuranceEndDateTime?.split('T')

    const customAttributes = {
      userId: user.userId,
      userType: user.userType,
      page_step: 'traveler_insurance_6.orderinfo',

      insurance_members: data.insured?.length || 0,

      insurance_startdate: insuranceStartDateTimeSplit?.[0],
      insurance_starttime: insuranceStartDateTimeSplit?.[1],

      insurance_enddate: insuranceEndDateTimeSplit?.[0],
      insurance_endtime: insuranceEndDateTimeSplit?.[1],

      insurance_nation: data.destinationCountryName,
      insurance_type: data.planName
    }

    sendEvent('airbridge.ecommerce.order.completed', {
      action: '해외여행보험_결제완료',
      label: data.planName || '-',
      semanticAttributes,
      customAttributes,
      value: -data.premium || 0
    })
  }

  /**************************************************************************************************
   * shuttle - 송영
   **************************************************************************************************/
  // 공항 송영 예약 출발지/도착지 Label 생성 (편도: "출발 / 도착", 왕복: "출발 <> 도착"). checkout/상세 응답 필드명이 달라 둘 다 지원
  const buildAirportTaxiLegLabel = (data: any) => {
    const legs = data?.legs
    const primaryLeg = legs?.find((leg: any) => leg.direction === 'AIRPORT_TO_DEST') ?? legs?.[0]
    const from = primaryLeg?.originAddress ?? primaryLeg?.pickupLocation?.address ?? '-'
    const to = primaryLeg?.destinationAddress ?? primaryLeg?.destinationLocation?.address ?? '-'

    return data?.tripType === 'ROUND_TRIP' ? `${from} <> ${to}` : `${from} / ${to}`
  }

  // 에어브릿지 공항 송영 결제 페이지 진입 트래킹 (주문 정보 로드 완료 시점)
  const trackAirportTaxiCheckout = async (data: any) => {
    const user: { userId: string; userType: string } = await getAccoutUserInfo()

    const destLeg = data.legs?.find((leg: any) => leg.direction === 'AIRPORT_TO_DEST')
    const depLeg = data.legs?.find((leg: any) => leg.direction === 'DEST_TO_AIRPORT')
    // 호텔↔호텔은 hotelAddress가 null로 오고 대신 originAddress/destinationAddress를 씀
    const htlLeg = data.legs?.find((leg: any) => leg.direction === 'HTL_TO_HTL')
    const firstLeg = data.legs?.[0]
    const paidAmount =
      data?.payment?.paidAmount ?? (data?.totalAmount || 0) - (data?.usedPoint || 0)

    const semanticAttributes = {
      currency: 'KRW',
      isRevenue: true,
      totalValue: paidAmount,
      originalTotalValue: data?.totalAmount || 0,
      products: [
        {
          productID: '송영',
          price: data?.amount?.payAmount || 0
        }
      ]
    }

    // 커스텀 어트리뷰트
    const customAttributes = {
      userType: user.userType,
      userId: user.userId,
      page_step: 'airport_sy_orderpage',
      Sy_nation: 'JP',
      Sy_arrival: destLeg?.airportCode || depLeg?.airportCode || '-',
      Sy_destination: destLeg?.hotelAddress || htlLeg?.destinationAddress || '-',
      Sy_departure: depLeg?.hotelAddress || htlLeg?.originAddress || '-',
      Sy_member: firstLeg?.passengers || 0,
      Sy_baggage: firstLeg?.luggage || 0,
      Sy_cartype: firstLeg?.vehicleModel || firstLeg?.vehicleType || '-',
      Sy_carseat: firstLeg?.childSeat ? 'Y' : 'N'
    }

    sendEvent('airbridge.initiateCheckout', {
      action: '송영_결제시작',
      label: buildAirportTaxiLegLabel(data),
      value: data?.amount?.payAmount || 0,
      semanticAttributes,
      customAttributes
    })
  }

  // 공항 송영 예약(legs) 기반 커스텀 어트리뷰트 추출 (결제완료/취소 이벤트 공용)
  const buildAirportTaxiLegAttributes = (legs: any) => {
    const destLeg = legs?.find((leg: any) => leg.direction === 'AIRPORT_TO_DEST')
    const depLeg = legs?.find((leg: any) => leg.direction === 'DEST_TO_AIRPORT')
    // 호텔↔호텔은 공항 방향이 없어 Sy_arrival은 그대로 '-', 출발/도착 주소만 채움
    const htlLeg = legs?.find((leg: any) => leg.direction === 'HTL_TO_HTL')
    const firstLeg = legs?.[0]

    return {
      Sy_nation: 'JP', // TODO: 국가 확장 시 leg 기반 매핑 필요
      Sy_arrival: destLeg?.airportCode || depLeg?.airportCode || '-',
      Sy_destination:
        destLeg?.destinationLocation?.address || htlLeg?.destinationLocation?.address || '-',
      Sy_departure: depLeg?.pickupLocation?.address || htlLeg?.pickupLocation?.address || '-',
      Sy_member: firstLeg?.passengers || 0,
      Sy_baggage: firstLeg?.luggage || 0,
      Sy_cartype: firstLeg?.vehicleModel || firstLeg?.vehicleType || '-',
      Sy_carseat: firstLeg?.childSeat ? 'Y' : 'N'
    }
  }

  // approve 응답의 legs(pickupLocation/destinationLocation 중첩객체, address가 비어있음)에
  // getReservationComplete 응답의 legs(originAddress/destAddress 평탄필드, 신뢰 가능)를 direction 기준으로 병합
  const mergeAirportTaxiAddressLegs = (legs: any, completeLegs?: any) => {
    if (!completeLegs) return legs

    return legs?.map((leg: any) => {
      const addr = completeLegs.find((cl: any) => cl.direction === leg.direction)
      return addr
        ? { ...leg, originAddress: addr.originAddress, destinationAddress: addr.destAddress }
        : leg
    })
  }

  // 에어브릿지 공항 송영 결제 완료 트래킹
  // completeLegs: getReservationComplete 응답의 legs(주소 신뢰 가능) — 없으면 data.legs 그대로 사용
  const trackAirportTaxiOrderCompleted = async (data: any, completeLegs?: any) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()
    const legs = mergeAirportTaxiAddressLegs(data?.legs, completeLegs)
    data = { ...data, legs }
    const paidAmount =
      data?.payment?.paidAmount ?? (data?.totalAmount || 0) - (data?.usedPoint || 0)

    // 시멘틱 어트리뷰트
    const semanticAttributes = {
      currency: 'KRW',
      isRevenue: true,
      totalValue: paidAmount,
      originalTotalValue: data?.totalAmount || 0,
      products: [
        {
          productID: '송영',
          price: paidAmount
        }
      ],
      transactionID: data?.orderNo || '-',
      transactionType: data?.payment?.payMethod || data?.payment?.pgProvider || '-'
    }

    // 커스텀 어트리뷰트
    const customAttributes = {
      userType: user.userType,
      userId: user.userId,
      page_step: 'airport_sy_payment_done',
      ...buildAirportTaxiLegAttributes(data?.legs)
    }

    sendEvent('airbridge.ecommerce.order.completed', {
      action: '송영_결제완료',
      label: buildAirportTaxiLegLabel(data),
      semanticAttributes,
      customAttributes,
      value: paidAmount
    })
  }

  // 에어브릿지 공항 송영 취소/환불 확정 트래킹
  const trackAirportTaxiOrderCanceled = async (data: any) => {
    // 통합계정 계정정보 가져오기
    const user: { userId: string; userType: string } = await getAccoutUserInfo()
    const paidAmount =
      data?.payment?.paidAmount ?? (data?.totalAmount || 0) - (data?.usedPoint || 0)

    // 시멘틱 어트리뷰트 (최종 결제금액/상품값 모두 음수로 적용)
    const semanticAttributes = {
      currency: 'KRW',
      isRevenue: true,
      totalValue: -paidAmount,
      originalTotalValue: -(data?.totalAmount || 0),
      products: [
        {
          productID: '송영',
          price: -paidAmount
        }
      ],
      transactionID: data?.orderNo || '-',
      transactionType: data?.payment?.payMethod || data?.payment?.pgProvider || '-'
    }

    // 커스텀 어트리뷰트
    const customAttributes = {
      userType: user.userType,
      userId: user.userId,
      page_step: 'airport_sy_payment_cancelled',
      ...buildAirportTaxiLegAttributes(data?.legs)
    }

    sendEvent('airbridge.ecommerce.order.canceled', {
      action: '송영_결제취소',
      label: buildAirportTaxiLegLabel(data),
      semanticAttributes,
      customAttributes,
      value: -paidAmount
    })
  }

  return {
    initAirbridge,
    sendEvent,
    trackHomeView,
    trackLogin,
    trackSignup,
    trackOrderInfo,
    trackOrderSuccess,
    trackAirportTaxiCheckout,
    trackAirportTaxiOrderCompleted,
    trackAirportTaxiOrderCanceled,
    trackRefundOder,
    trackSimAction,
    trackBenefit,
    trackCoupon,
    trackCouponReceive,
    trackInsuranceOrderInfo,
    trackInsuranceOrderSuccess,
    trackInsuranceOrderRefund
  }
}
